import { OptionalBool } from "@somewear/api";
import { z } from "zod";

import type { OptionalBoolValue } from "./grpc";
import { optionalBoolEnabled, toOptionalBool } from "./grpc";

type LiteralWithAtLeastTwoValues<T> = [
	z.ZodLiteral<T[keyof T]>,
	z.ZodLiteral<T[keyof T]>,
	...z.ZodLiteral<T[keyof T]>[],
];

export function protoToZodEnum<T extends { [K in keyof T]: number }>(protoEnum: T) {
	const literals = (Object.values(protoEnum) as number[]).map((value) => z.literal(value));

	if (literals.length < 2) {
		throw new Error("Enum must have at least 2 values for union");
	}

	return z.union(literals as LiteralWithAtLeastTwoValues<T>);
}

export type EnableableField<T> = {
	enabled: boolean;
	value: T;
};

export const optionalBoolSchema = protoToZodEnum(OptionalBool);

export function enableableField<T extends z.ZodType & { _def: { defaultValue: () => any } }>(
	schema: T,
	enabled = true,
) {
	return z
		.object({
			value: schema,
			enabled: z.boolean(),
		})
		.default({
			value: schema._def.defaultValue(),
			enabled: enabled,
		});
}

export function enableableFieldToOptionalBool(field: EnableableField<boolean>) {
	return field.enabled ? toOptionalBool(field.value) : OptionalBool.OPTIONALBOOLNONE;
}

export function toEnableableBooleanField(
	value: OptionalBoolValue,
	fallback?: boolean,
): EnableableField<boolean> {
	// When a fallback is provided, an unset value (OptionalBoolNone) resolves to the
	// fallback and the field stays enabled, so the form always shows a real value.
	if (value === OptionalBool.OPTIONALBOOLNONE && fallback !== undefined) {
		return { enabled: true, value: fallback };
	}
	return {
		enabled: value !== OptionalBool.OPTIONALBOOLNONE,
		value: optionalBoolEnabled(value),
	};
}
